import React, { useCallback, useEffect, useState } from "react";
import { Alert, DatePicker, Input, Modal, Select, Tag, message } from "antd";
import dayjs from "dayjs";
import { Button } from "../../../components/Component";
import { assetApis, AssetStatus } from "../../../api/Assets";
import { projectsApis } from "../../../api/projects";

/**
 * The ONE status write path for an asset — Assign / Return / Under Maintenance /
 * Retire. Assignment requires a project first (a machine is assigned to a site)
 * and then one of that project's users, so the Assigned To list is always
 * people who actually belong to the project.
 *
 * Used by both the list's row action and the editor's status card.
 */

const STATUS_OPTIONS: Array<{ value: AssetStatus; label: string; hint: string }> = [
  { value: "Available", label: "Available", hint: "Back in the yard — clears the assignment and project." },
  { value: "Assigned", label: "Assigned", hint: "Deployed to a project and a responsible person." },
  { value: "UnderMaintenance", label: "Under Maintenance", hint: "Out of service; keeps its project unless changed." },
  { value: "Retired", label: "Retired", hint: "Removed from use. The record stays for history but leaves all pickers." },
];

export const statusTagColor = (status?: string): string =>
  status === "Available"
    ? "green"
    : status === "Assigned"
    ? "blue"
    : status === "UnderMaintenance"
    ? "orange"
    : status === "Retired"
    ? "default"
    : "default";

export const statusLabel = (status?: string): string =>
  STATUS_OPTIONS.find((o) => o.value === status)?.label ?? status ?? "—";

const AssetStatusModal = ({
  open,
  asset,
  onClose,
  onDone,
}: {
  open: boolean;
  /** the row being changed — needs assetId, assetCode, assetName, status */
  asset: any | null;
  onClose: () => void;
  onDone?: () => void;
}) => {
  const [status, setStatus] = useState<AssetStatus>("Available");
  const [projectId, setProjectId] = useState<number | null>(null);
  const [assignedToUserId, setAssignedToUserId] = useState<number | null>(null);
  const [returnDate, setReturnDate] = useState<string | null>(null);
  const [location, setLocation] = useState("");
  const [remarks, setRemarks] = useState("");
  const [saving, setSaving] = useState(false);

  const [projectOptions, setProjectOptions] = useState<any[]>([]);
  const [userOptions, setUserOptions] = useState<any[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  useEffect(() => {
    if (!open || !asset) return;
    setStatus((asset.status as AssetStatus) || "Available");
    setProjectId(asset.projectId ? Number(asset.projectId) : null);
    setAssignedToUserId(asset.assignedToUserId ? Number(asset.assignedToUserId) : null);
    setReturnDate(asset.returnDate || null);
    setLocation(asset.location || "");
    setRemarks("");
  }, [open, asset]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const res = await projectsApis.getProjectList({
          page: 1,
          pageSize: 100,
          searchQuery: null,
          direction: "P",
          sortOrder: "DESC",
          pageDiff: 0,
          paginationId: null,
          projectStatus: "Active",
        });
        const list: any[] = res?.data?.list || [];
        setProjectOptions(
          list.map((project: any) => ({
            value: Number(project?.projectId),
            label: project?.projectTitle || `Project ${project?.projectId}`,
          })),
        );
      } catch {
        /* dropdown stays empty; the select shows no options */
      }
    })();
  }, [open]);

  // Assigned To = the picked project's own users.
  const loadUsers = useCallback(async (nextProjectId: number) => {
    setLoadingUsers(true);
    try {
      const res = await projectsApis.fetchProjectUsers(nextProjectId);
      const list: any[] = res?.data || [];
      setUserOptions(
        list.map((user: any) => ({
          value: Number(user.userId),
          label: user.userName || `User ${user.userId}`,
        })),
      );
    } catch {
      setUserOptions([]);
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  useEffect(() => {
    if (open && status === "Assigned" && projectId) loadUsers(projectId);
  }, [open, status, projectId, loadUsers]);

  const handleSave = async () => {
    if (status === "Assigned" && !projectId) {
      message.error("Pick the project the asset is being assigned to.");
      return;
    }
    if (status === "Assigned" && !assignedToUserId) {
      message.error("Pick who the asset is assigned to.");
      return;
    }
    setSaving(true);
    try {
      const res = await assetApis.changeStatus({
        assetId: asset.assetId,
        status,
        ...(status === "Assigned"
          ? {
              assignedToUserId: assignedToUserId as number,
              projectId: projectId as number,
              returnDate: returnDate || undefined,
              location: location || undefined,
            }
          : { location: location || undefined }),
        remarks: remarks || undefined,
      });
      if (res?.status) {
        message.success(res?.data?.message || `Status changed to ${statusLabel(status)}.`);
        onDone?.();
        onClose();
      } else {
        message.error(res?.data?.message || res?.message || "Could not change the status.");
      }
    } finally {
      setSaving(false);
    }
  };

  const hint = STATUS_OPTIONS.find((o) => o.value === status)?.hint;

  return (
    <Modal
      title={
        <span>
          Change Status —{" "}
          <span style={{ color: "#2557d6" }}>
            {asset?.assetCode} · {asset?.assetName}
          </span>
        </span>
      }
      open={open}
      onCancel={onClose}
      footer={[
        <Button key="cancel" color="light" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="save"
          color={status === "Retired" ? "danger" : "primary"}
          disabled={saving}
          onClick={handleSave}
        >
          {saving ? "Saving…" : status === "Retired" ? "Retire Asset" : "Change Status"}
        </Button>,
      ]}
    >
      <div className="mb-3">
        <label className="form-label mb-1">New status</label>
        <Select
          value={status}
          onChange={(v) => setStatus(v)}
          options={STATUS_OPTIONS.map(({ value, label }) => ({ value, label }))}
          style={{ width: "100%" }}
        />
        {hint ? (
          <div className="text-soft" style={{ fontSize: 12, marginTop: 4 }}>
            {hint}
          </div>
        ) : null}
      </div>

      {status === "Assigned" && (
        <>
          <div className="mb-3">
            <label className="form-label mb-1">
              Project <span style={{ color: "#e85347" }}>*</span>
            </label>
            <Select
              value={projectId}
              onChange={(v) => {
                setProjectId(v);
                setAssignedToUserId(null);
              }}
              options={projectOptions}
              showSearch
              optionFilterProp="label"
              placeholder="Where is it deployed?"
              style={{ width: "100%" }}
            />
          </div>
          <div className="mb-3">
            <label className="form-label mb-1">
              Assigned To <span style={{ color: "#e85347" }}>*</span>
            </label>
            <Select
              value={assignedToUserId}
              onChange={(v) => setAssignedToUserId(v)}
              options={userOptions}
              loading={loadingUsers}
              disabled={!projectId}
              showSearch
              optionFilterProp="label"
              placeholder={projectId ? "Responsible person" : "Pick the project first"}
              style={{ width: "100%" }}
            />
          </div>
          <div className="mb-3">
            <label className="form-label mb-1">Expected Return Date</label>
            <DatePicker
              showTime={{ format: "HH:mm" }}
              format="DD/MM/YYYY HH:mm"
              value={returnDate ? dayjs(returnDate) : null}
              onChange={(v) => setReturnDate(v ? v.toISOString() : null)}
              style={{ width: "100%" }}
            />
          </div>
        </>
      )}

      <div className="mb-3">
        <label className="form-label mb-1">Location</label>
        <Input
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          placeholder="e.g. Adit-1A portal, Central yard"
        />
      </div>

      <div className="mb-1">
        <label className="form-label mb-1">Remarks</label>
        <Input.TextArea
          rows={2}
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          maxLength={500}
        />
      </div>

      {status === "Retired" && (
        <Alert
          type="warning"
          showIcon
          className="mt-2"
          message="Retiring removes this asset from every picker (including machinery norms). The record and its history remain."
        />
      )}

      {asset?.status ? (
        <div className="text-soft mt-2" style={{ fontSize: 12 }}>
          Current: <Tag color={statusTagColor(asset.status)}>{statusLabel(asset.status)}</Tag>
        </div>
      ) : null}
    </Modal>
  );
};

export default AssetStatusModal;
